import { ReactNode, useState } from 'react'

import { Box, type Colors } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import {
  ContractsEmployeesContractVersionFragment,
  ContractsEmployeesEmployeeFragment,
  ContractsEmployeesReferenceContractFragment,
} from 'generated/resources/graphql'
import { uniqueId } from 'lodash'

import { useEffect } from 'modules/core/hooks/useEffect'

import useFetchAdditionalCompensations from 'modules/contracts/hooks/useFetchAdditionalCompensations'
import type ContractFlow from 'modules/contracts/lib/ContractFlow'
import { useContractFlow } from 'modules/contracts/lib/contractFlowContext'
import {
  LegalEntityContractData,
  LegalEntityContractDataProvider,
} from 'modules/contracts/lib/legalEntityContractDataContext'

import Footer from './_Footer'
import Header from './_Header'
import { Menu } from './_Menu'

interface Props {
  translations: { [key: string]: string }
  form: Form
  contractVersion?:
    | ContractsEmployeesContractVersionFragment
    | ContractsEmployeesReferenceContractFragment
    | null
  legalEntityContractData: LegalEntityContractData
  title: string
  flow: ContractFlow
  footerContent: ReactNode
  isCurrentStepCompleted: boolean
  isSubmitting: boolean
  background?: Colors
  onClose: () => void
  onSubmit: () => void
  employee: ContractsEmployeesEmployeeFragment
}

export const FlowPageContent = ({
  translations,
  form,
  contractVersion,
  legalEntityContractData,
  title,
  flow,
  isCurrentStepCompleted,
  isSubmitting,
  footerContent,
  background,
  onClose,
  onSubmit,
  employee,
}: Props) => {
  const { compensations, setCompensations, setInitialCompensations } = useContractFlow()

  const {
    loading: contractDataLoading,
    spanishContractData,
    frenchContractData,
    germanContractData,
    portugueseContractData,
    commonContractData,
  } = legalEntityContractData

  const additionalCompensations = useFetchAdditionalCompensations(
    contractVersion?.id ? [contractVersion.id] : undefined
  )
  const [currentStep, setCurrentStep] = useState<number>(flow.currentStepNumber)

  const onBackButtonClick = () => {
    if (flow.hasPreviousStep()) {
      setCurrentStep(flow.previousStep(form))
    }
  }
  const onNextButtonClick = () => {
    if (flow.hasNextStep()) {
      setCurrentStep(flow.nextStep(form))
    }
  }
  useEffect(() => {
    if (!additionalCompensations.loading && !compensations) {
      setCompensations(
        additionalCompensations.additionalCompensations.models.map((model) => {
          model.attributes.temporal_id = uniqueId()
          return model
        })
      )
      setInitialCompensations(additionalCompensations.additionalCompensations.models)
    }
  }, [additionalCompensations.loading]) // eslint-disable-line react-hooks/exhaustive-deps

  // Ensure that all the data needed for this component is available before going forward
  if (contractDataLoading) return null

  return (
    <LegalEntityContractDataProvider
      spanishContractData={spanishContractData}
      frenchContractData={frenchContractData}
      germanContractData={germanContractData}
      portugueseContractData={portugueseContractData}
      commonContractData={commonContractData}
    >
      <Box
        width="full"
        flexGrow
        overflowY="hidden"
        flexDirection="column"
        style={{ height: 'calc(100dvh - 220px)' }} // TODO: 220px is the height of the header. We should find a better way to do this.
      >
        <Box height="s64" width="full">
          <Header
            onClose={onClose}
            flow={flow}
            background={background}
            employee={employee}
            title={title}
          />
        </Box>
        <Box flexDirection="row" flexGrow overflowY="hidden" width="full">
          <Box width="s360" paddingY="s40" flexNoShrink overflowY="auto">
            <Menu
              flow={flow}
              key={currentStep}
              setCurrentStep={setCurrentStep}
              steps={flow.steps}
            />
          </Box>
          <Box paddingRight="s48" paddingY="s40" overflowY="auto" flexGrow>
            {flow.currentStep.component}
          </Box>
        </Box>
        <Box height="s64" width="full">
          <Footer
            translations={translations}
            content={footerContent}
            form={form}
            flow={flow}
            isCurrentStepCompleted={isCurrentStepCompleted}
            isSubmitting={isSubmitting}
            onBackButtonClick={onBackButtonClick}
            onNextButtonClick={onNextButtonClick}
            background={background}
            onSubmit={onSubmit}
          />
        </Box>
      </Box>
    </LegalEntityContractDataProvider>
  )
}
